import React, { Component } from 'react';
import FreightByLoBBar from "./FreightByLoBBar";
import ShipToStateTable from "./ShipToStateTable";
import SalesPersonStateTable from "./SalesPersonStateTable";
import USAMap from "./USAMap";
import { getInformationWithDataDashboard as getInformationWithData } from "../../services/CABE";
import { Menu } from "../../components/Menu/Menu";
import LoadingWindow from '../../components/LoadingWindow';
import "../../css/dashboard.css";
import "../../css/logistics-v2-responsive.css"; // RESP-2026-11-03 (tablet/phone only; the lg-* classNames do nothing on their own)
import { BsTruck } from 'react-icons/bs';
import { AiOutlineReload } from 'react-icons/ai';

// ─── State coordinates for USA map ──────────────────────────────────────────
const STATE_COORDS = {
  AL:{lat:32.8067,lng:-86.7911,label:"Alabama"},AK:{lat:61.3707,lng:-152.4044,label:"Alaska"},
  AZ:{lat:33.7298,lng:-111.4312,label:"Arizona"},AR:{lat:34.9697,lng:-92.3731,label:"Arkansas"},
  CA:{lat:36.7783,lng:-119.4179,label:"California"},CO:{lat:39.5501,lng:-105.7821,label:"Colorado"},
  CT:{lat:41.6032,lng:-73.0877,label:"Connecticut"},DE:{lat:38.9108,lng:-75.5277,label:"Delaware"},
  FL:{lat:27.6648,lng:-81.5158,label:"Florida"},GA:{lat:32.1574,lng:-82.9071,label:"Georgia"},
  HI:{lat:19.8987,lng:-155.6659,label:"Hawaii"},ID:{lat:44.0682,lng:-114.742,label:"Idaho"},
  IL:{lat:40.6331,lng:-89.3985,label:"Illinois"},IN:{lat:40.2672,lng:-86.1349,label:"Indiana"},
  IA:{lat:41.878,lng:-93.0977,label:"Iowa"},KS:{lat:39.0119,lng:-98.4842,label:"Kansas"},
  KY:{lat:37.8393,lng:-84.27,label:"Kentucky"},LA:{lat:31.2448,lng:-92.145,label:"Louisiana"},
  ME:{lat:45.2538,lng:-69.4455,label:"Maine"},MD:{lat:39.0458,lng:-76.6413,label:"Maryland"},
  MA:{lat:42.4072,lng:-71.3824,label:"Massachusetts"},MI:{lat:44.3148,lng:-85.6024,label:"Michigan"},
  MN:{lat:46.7296,lng:-94.6859,label:"Minnesota"},MS:{lat:32.3547,lng:-89.3985,label:"Mississippi"},
  MO:{lat:37.9643,lng:-91.8318,label:"Missouri"},MT:{lat:46.8797,lng:-110.3626,label:"Montana"},
  NE:{lat:41.4925,lng:-99.9018,label:"Nebraska"},NV:{lat:38.8026,lng:-116.4194,label:"Nevada"},
  NH:{lat:43.1939,lng:-71.5724,label:"New Hampshire"},NJ:{lat:40.0583,lng:-74.4057,label:"New Jersey"},
  NM:{lat:34.5199,lng:-105.8701,label:"New Mexico"},NY:{lat:43.2994,lng:-74.2179,label:"New York"},
  NC:{lat:35.7596,lng:-79.0193,label:"North Carolina"},ND:{lat:47.5515,lng:-101.002,label:"North Dakota"},
  OH:{lat:40.4173,lng:-82.9071,label:"Ohio"},OK:{lat:35.0078,lng:-97.0929,label:"Oklahoma"},
  OR:{lat:43.8041,lng:-120.5542,label:"Oregon"},PA:{lat:41.2033,lng:-77.1945,label:"Pennsylvania"},
  RI:{lat:41.5801,lng:-71.4774,label:"Rhode Island"},SC:{lat:33.8361,lng:-81.1637,label:"South Carolina"},
  SD:{lat:43.9695,lng:-99.9018,label:"South Dakota"},TN:{lat:35.5175,lng:-86.5804,label:"Tennessee"},
  TX:{lat:31.9686,lng:-99.9018,label:"Texas"},UT:{lat:39.321,lng:-111.0937,label:"Utah"},
  VT:{lat:44.5588,lng:-72.5778,label:"Vermont"},VA:{lat:37.4316,lng:-78.6569,label:"Virginia"},
  WA:{lat:47.7511,lng:-120.7401,label:"Washington"},WV:{lat:38.5976,lng:-80.4549,label:"West Virginia"},
  WI:{lat:43.7844,lng:-88.7879,label:"Wisconsin"},WY:{lat:43.0759,lng:-107.2903,label:"Wyoming"},
  DC:{lat:38.9072,lng:-77.0369,label:"Washington, DC"}
};

// ─── Helpers ────────────────────────────────────────────────────────────────
function toNumber(v) {
  if (v === null || v === undefined) return 0;
  const n = parseFloat(String(v));
  return isNaN(n) ? 0 : n;
}

function fmtUSD(n) {
  return '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtPct(n) {
  return (n >= 0 ? '' : '-') + Math.abs(Math.round(n)) + '%';
}

// Map DB snake_case → camelCase/legacy names that sub-components expect
function adaptInvoice(inv) {
  return {
    ...inv,
    shiptostate:  inv.ship_to_state,
    billtoname:   inv.bill_to_name,
    freightamt:   toNumber(inv.freight_amt),
    carrier_price: toNumber(inv.carrier_price),
    salespersonno: inv.salesperson_no,
    udf_ruta:     inv.udf_ruta || '',
  };
}

function buildStateMarkers(invoices) {
  const counts = {};
  for (const inv of invoices) {
    const st = inv.shiptostate ? String(inv.shiptostate).trim().toUpperCase() : '';
    if (!st) continue;
    counts[st] = (counts[st] || 0) + 1;
  }
  return Object.keys(counts).map(st => {
    const meta = STATE_COORDS[st];
    if (!meta) return null;
    return { lat: meta.lat, lng: meta.lng, value: counts[st], label: meta.label + ' (' + st + ')' };
  }).filter(Boolean);
}

function buildDemoRows(invoices) {
  const groups = {};
  for (const inv of invoices) {
    const st     = inv.shiptostate ? String(inv.shiptostate).trim().toUpperCase() : 'N/A';
    const route  = inv.udf_ruta || 'N/A';
    const billTo = inv.billtoname || 'N/A';
    const key    = st + '||' + route + '||' + billTo;
    if (!groups[key]) {
      groups[key] = { ShipToState: st, ProductLineDesc: route, BillToName: billTo, Envios: 0, FreightAmt: 0, _carrierSum: 0 };
    }
    groups[key].Envios      += 1;
    groups[key].FreightAmt  += toNumber(inv.freightamt);
    groups[key]._carrierSum += toNumber(inv.carrier_price);
  }
  return Object.values(groups).map(r => {
    const cost  = r.FreightAmt;
    const payed = r._carrierSum;
    const diffPct  = cost !== 0 ? ((payed - cost) / cost) * 100 : 0;
    const difference = cost - payed;
    const subsidy    = payed !== 0 ? (difference / payed) * 100 : 0;
    return {
      ShipToState: r.ShipToState, ProductLineDesc: r.ProductLineDesc, BillToName: r.BillToName,
      Envios: r.Envios, FreightAmt: Math.round(r.FreightAmt * 100) / 100,
      diffPct: Math.round(diffPct), payed: Math.round(payed),
      difference: Math.round(difference), subsidy: Math.round(subsidy),
    };
  })
    // 2026-09-10, client's ask — "muestra únicamente los clientes que tengan
    // una transacción": a row with $0 charged AND $0 paid to the carrier has
    // no real freight transaction (Envios can still be > 0 — deliveries that
    // never got a freight cost captured — but that's not what the client is
    // asking to see here).
    .filter(r => r.FreightAmt !== 0 || r.payed !== 0)
    .sort((a, b) => (b.FreightAmt || 0) - (a.FreightAmt || 0));
}

function buildSalesPersonRows(invoices) {
  const groups = {};
  for (const inv of invoices) {
    const sp     = inv.salespersonno ? String(inv.salespersonno).trim().toUpperCase() : 'N/A';
    const route  = inv.udf_ruta || 'N/A';
    const billTo = inv.billtoname || 'N/A';
    const key    = sp + '||' + route + '||' + billTo;
    if (!groups[key]) {
      groups[key] = { SalesPerson: sp, ProductLineDesc: route, BillToName: billTo, FleteCobrado: 0, FacturaCarrier: 0, SubsidioCantidad: 0 };
    }
    const freight = toNumber(inv.freightamt);
    const carrier = toNumber(inv.carrier_price);
    groups[key].FleteCobrado    += freight;
    groups[key].FacturaCarrier  += carrier;
    groups[key].SubsidioCantidad += (carrier - freight);
  }
  return Object.values(groups).map(r => {
    const pct = r.FleteCobrado !== 0 ? (r.SubsidioCantidad / r.FleteCobrado) * 100 : 0;
    return {
      SalesPerson: r.SalesPerson, ProductLineDesc: r.ProductLineDesc, BillToName: r.BillToName,
      Facturado: 0, FleteCobrado: Math.round(r.FleteCobrado),
      FacturaCarrier: Math.round(r.FacturaCarrier),
      SubsidioCantidad: Math.round(r.SubsidioCantidad),
      SubsidioProcentaje: Math.round(pct),
    };
  })
    // 2026-09-10, client's ask — same "only clients with a real transaction"
    // filter as buildDemoRows above.
    .filter(r => r.FleteCobrado !== 0 || r.FacturaCarrier !== 0)
    .sort((a, b) => (b.SubsidioCantidad || 0) - (a.SubsidioCantidad || 0));
}

// CARRIERDATA-2026-11-03 — the dashboard used to total the freight CHARGED of every invoice but only the carrier
// amount of the few that already had a carrier captured, so "Diferencia" and "Subsidio %" were wildly inflated
// (e.g. $25,923 charged vs $811 paid -> 3096%). Totals now use only invoices whose carrier data is complete:
// carrier + carrier amount + folio. With an older backend that does not send `carrier_folio` yet, the folio
// cannot be checked, so it is not required (carrier + amount only).
// Revert = make buildViews() use `allInvoices` instead of `scope` (backup: /private/tmp/LogisticsV2.js.bak2-2026-11-03).
function hasCompleteCarrierData(inv) {
  const hasCarrier = !!(inv.carrier_name || inv.carrier_id);
  const hasAmount  = toNumber(inv.carrier_price) > 0;
  const hasFolio   = inv.carrier_folio === undefined ? true : String(inv.carrier_folio || '').trim() !== '';
  return hasCarrier && hasAmount && hasFolio;
}

// Everything the page derives from the invoices. The map keeps ALL invoices (it shows where shipments go).
function buildViews(allInvoices, includeIncomplete) {
  const scope = includeIncomplete ? allInvoices : allInvoices.filter(hasCompleteCarrierData);
  return {
    stats:           computeStats(scope),
    markers:         buildStateMarkers(allInvoices),
    tableByStates:   buildDemoRows(scope),
    tableBySalesman: buildSalesPersonRows(scope),
  };
}

function computeStats(invoices) {
  let freightCharged = 0, freightPayed = 0, totalSales = 0;
  let pallet = 0, distributor = 0, supermarket = 0, warehouse = 0;

  for (const inv of invoices) {
    const fc  = toNumber(inv.freightamt);
    const fp  = toNumber(inv.carrier_price);
    const ts  = toNumber(inv.non_taxable_sales_amt || inv.non_taxable_amt);
    const lob = (inv.udf_ruta || '').toLowerCase();

    freightCharged += fc;
    freightPayed   += fp;
    totalSales     += ts;

    if      (lob.includes('pallet'))      pallet      += fc;
    else if (lob.includes('distributor')) distributor  += fc;
    else if (lob.includes('supermarket')) supermarket  += fc;
    else if (lob.includes('warehouse'))   warehouse    += fc;
  }

  const difference     = freightCharged - freightPayed;
  const subsidyPct     = freightPayed   !== 0 ? (difference / freightPayed)   * 100 : 0;
  const subsidySalesPct= totalSales     !== 0 ? (difference / totalSales)     * 100 : 0;
  const payedSalesPct  = totalSales     !== 0 ? (freightPayed / totalSales)   * 100 : 0;

  return {
    freight_charged:       Math.round(freightCharged),
    freight_payed:         Math.round(freightPayed),
    difference:            Math.round(difference),
    subsidy_porcent:       Math.round(subsidyPct),
    subsidy_porcent_sales: Math.round(subsidySalesPct),
    payed_sales_porcent:   Math.round(payedSalesPct),
    freight_pallet:        Math.round(pallet),
    freight_distributor:   Math.round(distributor),
    freight_supermarket:   Math.round(supermarket),
    freight_warehouse:     Math.round(warehouse),
  };
}

// ─── Component ───────────────────────────────────────────────────────────────
export default class LogisticsV2 extends Component {
  constructor(props) {
    super(props);
    this.ModalLoading = React.createRef();
    this.state = {
      year:    String(new Date().getFullYear()),
      month:   String(new Date().getMonth() + 1).padStart(2, '0'),
      salesMan: 'all',
      businessLine: 'all',
      invoices: [],
      stats: {},
      markers: [],
      tableByStates: [],
      tableBySalesman: [],
      includeIncomplete: false, // CARRIERDATA-2026-11-03
    };
  }

  componentDidMount() {
    this.loadDashboard();
    this.watchMenuHeight(); // RESP-2026-11-03
  }

  componentWillUnmount() {
    this.unwatchMenuHeight(); // RESP-2026-11-03
  }

  // RESP-2026-11-03 — the top menu is position:fixed and, on a phone, its height changes per user (1 row, or 2 when the
  // bell / profile icons wrap). A fixed 70px page offset left the hero hidden under it, so the real height is measured
  // and published as --lg-menu-h (used only by css/logistics-v2-responsive.css, phone width). Revert = remove the 3
  // watch/unwatch hooks + methods and the .lg-page padding rule.
  watchMenuHeight() {
    const apply = () => {
      const nav = document.querySelector('nav.fixed-top');
      const root = document.querySelector('.lg-page');
      if (root) root.style.setProperty('--lg-menu-h', (nav ? nav.offsetHeight : 70) + 'px');
    };
    this.__applyMenuH = apply;
    apply();
    window.addEventListener('resize', apply);
    this.__menuTimers = [setTimeout(apply, 400), setTimeout(apply, 1500)]; // the menu can finish rendering after mount
    const nav = document.querySelector('nav.fixed-top');
    if (nav && typeof ResizeObserver !== 'undefined') {
      this.__menuObserver = new ResizeObserver(apply);
      this.__menuObserver.observe(nav);
    }
  }

  unwatchMenuHeight() {
    if (this.__applyMenuH) window.removeEventListener('resize', this.__applyMenuH);
    (this.__menuTimers || []).forEach(clearTimeout);
    if (this.__menuObserver) this.__menuObserver.disconnect();
  }

  async loadDashboard() {
    const { year, month, salesMan, businessLine } = this.state;
    this.ModalLoading.current.showState(true);

    const res = await getInformationWithData('/freight/invoices/dashboard', { year, month, salesMan, businessLine });
    this.ModalLoading.current.showState(false);

    if (!res || !res.status || res.status.code !== 1) return;

    const raw = Array.isArray(res.data) ? res.data : [];
    const invoices = raw.map(adaptInvoice);

    this.setState({ invoices, ...buildViews(invoices, this.state.includeIncomplete) });
  }

  // CARRIERDATA-2026-11-03 — switch between "only invoices with complete carrier data" (default) and all of them.
  toggleIncludeIncomplete = () => {
    const include = !this.state.includeIncomplete;
    this.setState({ includeIncomplete: include, ...buildViews(this.state.invoices, include) });
  };

  onFilterChange(key, value) {
    this.setState({ [key]: value }, () => this.loadDashboard());
  }

  render() {
    const { year, month, salesMan, businessLine, stats, markers, tableByStates, tableBySalesman, invoices, includeIncomplete } = this.state;
    const completeCount = invoices.filter(hasCompleteCarrierData).length;
    const missingCount  = invoices.length - completeCount;

    const kpis = [
      {
        label: 'Flete Cobrado',
        value: fmtUSD(stats.freight_charged || 0),
        sub: 'Charged to customers',
        variant: 'status-primary',
      },
      {
        label: 'Flete Pagado',
        value: fmtUSD(stats.freight_payed || 0),
        sub: 'Paid to carriers',
        variant: 'status-danger',
      },
      {
        label: 'Diferencia',
        value: fmtUSD(stats.difference || 0),
        sub: 'F.Cobrado − F.Pagado',
        variant: stats.difference >= 0 ? 'status-success' : 'status-danger',
      },
      {
        label: 'Subsidio %',
        value: fmtPct(stats.subsidy_porcent || 0),
        sub: 'Diferencia / F.Pagado',
        variant: 'status-warning',
      },
      {
        label: 'F.Pagado / Ventas',
        value: fmtPct(stats.payed_sales_porcent || 0),
        sub: 'Freight cost as % of sales',
        variant: 'status-primary',
      },
    ];

    const labelStyle = {
      fontSize: '0.75rem', fontWeight: 700, color: '#64748B',
      textTransform: 'uppercase', letterSpacing: '0.05em',
      display: 'block', marginBottom: '4px',
    };
    const selectStyle = {
      borderRadius: '8px', border: '1.5px solid #E2E8F0',
      fontSize: '0.875rem',
    };

    return (
      <div className="dash-page container-fluid pb-5 lg-page" style={{ paddingTop: '70px' }}>
        <Menu />
        <LoadingWindow ref={this.ModalLoading} />

        {/* Hero */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="lg-hero" style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #2563EB 100%)',
              borderRadius: '16px', padding: '2rem 2.5rem',
              display: 'flex', alignItems: 'center', gap: '1.25rem',
              boxShadow: '0 4px 24px rgba(37,99,235,0.18)',
            }}>
              <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '14px', padding: '0.875rem' }}>
                <BsTruck style={{ color: 'white', fontSize: '2rem' }} />
              </div>
              <div>
                <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.6rem', margin: 0, letterSpacing: '-0.02em' }}>
                  Logistics Dashboard
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.65)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
                  Freight analysis · Carrier cost · Subsidy by route
                </p>
              </div>
              <div className="lg-hero-pill" style={{ marginLeft: 'auto' }}>
                <span style={{
                  background: 'rgba(255,255,255,0.15)', color: 'white', borderRadius: '999px',
                  padding: '0.35rem 1rem', fontSize: '0.8rem', fontWeight: 700,
                }}>
                  {includeIncomplete ? `${invoices.length} invoices` : `${completeCount} of ${invoices.length} invoices`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* What the totals are based on (CARRIERDATA-2026-11-03) */}
        {invoices.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="lg-note" style={{
                background: 'rgba(37,99,235,0.06)', border: '1px solid #BFDBFE', borderRadius: '10px',
                padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
              }}>
                <span style={{ color: '#1e40af', fontSize: '0.875rem', fontWeight: 600, flex: '1 1 280px' }}>
                  {includeIncomplete
                    ? `Totals include all ${invoices.length} invoices, even the ${missingCount} without complete carrier data (carrier, amount and folio).`
                    : `Totals use the ${completeCount} of ${invoices.length} invoices with complete carrier data (carrier, amount and folio). The other ${missingCount} are left out so the subsidy is not inflated.`}
                </span>
                {missingCount > 0 && (
                  <button type="button" onClick={this.toggleIncludeIncomplete} style={{
                    background: 'white', color: '#1e40af', border: '1px solid #BFDBFE', borderRadius: '8px',
                    padding: '0.35rem 0.9rem', fontSize: '0.8125rem', fontWeight: 700, cursor: 'pointer',
                  }}>
                    {includeIncomplete ? 'Only complete data' : 'Include all invoices'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* KPI cards */}
        <div className="row g-3 mb-4 lg-kpis">
          {kpis.map((k, i) => (
            <div className="col-12 col-sm-6 col-md-4 col-xl" key={i}>
              <div className={`metric-card ${k.variant}`}>
                <div className="metric-label"><BsTruck style={{ marginRight: 4 }} />{k.label}</div>
                <div className="metric-value">{k.value}</div>
                <div className="metric-change">{k.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card" style={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <div className="card-body py-3">
                <div className="row g-3 align-items-end lg-filters">
                  <div className="col-6 col-md-3">
                    <label style={labelStyle}>Year</label>
                    <select className="form-select form-select-sm" style={selectStyle}
                      value={year}
                      onChange={e => this.onFilterChange('year', e.target.value)}
                    >
                      <option value="2026">2026</option>
                      <option value="2025">2025</option>
                      <option value="2024">2024</option>
                    </select>
                  </div>
                  <div className="col-6 col-md-3">
                    <label style={labelStyle}>Month</label>
                    <select className="form-select form-select-sm" style={selectStyle}
                      value={month}
                      onChange={e => this.onFilterChange('month', e.target.value)}
                    >
                      <option value="all">All</option>
                      {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, i) => (
                        <option key={m} value={m}>
                          {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6 col-md-3">
                    <label style={labelStyle}>Salesperson</label>
                    <select className="form-select form-select-sm" style={selectStyle}
                      value={salesMan}
                      onChange={e => this.onFilterChange('salesMan', e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="AM">AM</option>
                      <option value="RR">RR</option>
                      <option value="WW">WW</option>
                    </select>
                  </div>
                  <div className="col-6 col-md-3">
                    <label style={labelStyle}>Business Line</label>
                    <select className="form-select form-select-sm" style={selectStyle}
                      value={businessLine}
                      onChange={e => this.onFilterChange('businessLine', e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="Pallet">Pallet</option>
                      <option value="Distributor">Distributor</option>
                      <option value="Supermarket">Supermarket</option>
                      <option value="Warehouse">Warehouse</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-xl-4 d-flex flex-column gap-3">
            <div className="card" style={{ borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden', flex: 1 }}>
              <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)', padding: '0.75rem 1.25rem' }}>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '0.8125rem' }}>Por Línea de Negocio</span>
              </div>
              <div className="card-body">
                <FreightByLoBBar
                  labels={['Pallet', 'Distributor', 'Supermarket', 'Warehouse']}
                  values={[stats.freight_pallet || 0, stats.freight_distributor || 0, stats.freight_supermarket || 0, stats.freight_warehouse || 0]}
                />
              </div>
            </div>
            <div className="card" style={{ borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden', flex: 1 }}>
              <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)', padding: '0.75rem 1.25rem' }}>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '0.8125rem' }}>Flete Cobrado vs Pagado</span>
              </div>
              <div className="card-body">
                <FreightByLoBBar
                  labels={['Flete cobrado', 'Flete pagado']}
                  values={[stats.freight_charged || 0, stats.freight_payed || 0]}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-xl-8">
            <div className="card" style={{ borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden', height: '100%' }}>
              <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)', padding: '0.75rem 1.25rem' }}>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '0.8125rem' }}>Distribución en USA</span>
              </div>
              <div className="card-body">
                <USAMap points={markers} onReady={() => {}} />
              </div>
            </div>
          </div>
        </div>

        {/* Tables */}
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <ShipToStateTable rows={tableByStates} onExport={() => {}} />
          </div>
          <div className="col-12 col-lg-6">
            <SalesPersonStateTable rows={tableBySalesman} onExport={() => {}} />
          </div>
        </div>
      </div>
    );
  }
}
